import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormBuilderWorkspace from './FormBuilderWorkspace'
import { emptyDefinition, emptySettings } from './formBuilderModel'

export default function FormBuilder() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(!!id)
  const [saving, setSaving] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [creating, setCreating] = useState(!id)
  const [draftName, setDraftName] = useState('')
  const [draftFacing, setDraftFacing] = useState('internal')

  const openForm = useCallback(async (formId) => {
    if (!formId) {
      setForm(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    setInfo('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/${formId}`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to open form')
      setForm(j.data)
      setCreating(false)
    } catch (e) {
      setError(e.message)
      setForm(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (id) openForm(id)
    else {
      setForm(null)
      setCreating(true)
      setLoading(false)
    }
  }, [id, openForm])

  const createForm = async () => {
    const name = draftName.trim()
    if (!name) return
    const res = await authFetch(getLocalApiUrl('/api/forms'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, facing: draftFacing }),
    })
    const j = await res.json()
    if (!res.ok || !j.success) {
      setError(j.error || 'Create failed')
      return
    }
    setDraftName('')
    navigate(`/admin/form-builder/${j.data.id}`)
  }

  const save = async () => {
    if (!form) return
    setSaving(true)
    setError('')
    setInfo('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/${form.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          slug: form.slug,
          facing: form.facing,
          workingDefinition: form.workingDefinition || emptyDefinition(),
          workingSettings: form.workingSettings || emptySettings(),
        }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Save failed')
      setForm(j.data)
      setInfo('Draft saved')
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const publish = async () => {
    if (!form) return
    await save()
    const res = await authFetch(getLocalApiUrl(`/api/forms/${form.id}/publish`), { method: 'POST' })
    const j = await res.json()
    if (!res.ok || !j.success) {
      setError(j.error || 'Publish failed')
      return
    }
    setForm(j.data.form)
    setInfo(`Published version ${j.data.version.versionNo}`)
  }

  const translateLocale = async (targetLocale, { overwrite = false } = {}) => {
    if (!form) return
    setTranslating(true)
    setError('')
    setInfo('')
    try {
      await save()
      const res = await authFetch(getLocalApiUrl(`/api/forms/${form.id}/translate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetLocale, sourceLocale: 'en', overwrite }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Translation failed')
      setForm(j.data)
      const n = j.stats?.translated ?? j.stats?.count ?? 0
      setInfo(`Translated ${n} string${n === 1 ? '' : 's'} to ${targetLocale}${j.provider ? ` (${j.provider})` : ''}`)
    } catch (e) {
      setError(e.message)
    } finally {
      setTranslating(false)
    }
  }

  const startTest = async () => {
    if (!form) return
    setSaving(true)
    setError('')
    setInfo('')
    try {
      await save()
      const res = await authFetch(getLocalApiUrl(`/api/forms/${form.id}/test`), { method: 'POST' })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Could not start a test run')
      const assignmentId = j.data?.assignment?.id
      if (!assignmentId) throw new Error('Test run did not return an assignment')
      navigate(`/compliance-safe/forms/assignments/${assignmentId}`)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Form Builder</h1>
          <p className="text-sm text-slate-500 mt-1">
            Design internal extra-data forms and patron-send forms. Publish a version before assigning.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms')}>Forms</Button>
          <Button onClick={() => { setCreating(true); setDraftName(''); setDraftFacing('internal'); navigate('/admin/form-builder') }}>
            <PlusIcon className="w-4 h-4 mr-1" /> New form
          </Button>
        </div>
      </div>

      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {info ? <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-800">{info}</div> : null}

      {creating && !form ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 max-w-lg">
          <div className="text-sm font-semibold text-slate-900 mb-3">New form</div>
          <label className="block text-xs font-semibold text-slate-600 mb-3">
            Name
            <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={draftName} onChange={(e) => setDraftName(e.target.value)} autoFocus />
          </label>
          <div className="flex gap-2 mb-4">
            {[['internal', 'Internal — staff fill'], ['patron', 'Patron — send invite']].map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setDraftFacing(v)}
                className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${draftFacing === v ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={createForm} disabled={!draftName.trim()}>Create</Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/compliance-safe/forms')}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : null}

      {form ? (
        <FormBuilderWorkspace
          form={form}
          definition={form.workingDefinition || emptyDefinition()}
          settings={form.workingSettings || emptySettings()}
          onDefinitionChange={(fn) => setForm((prev) => ({ ...prev, workingDefinition: fn(prev.workingDefinition || emptyDefinition()) }))}
          onSettingsChange={(fn) => setForm((prev) => ({ ...prev, workingSettings: fn(prev.workingSettings || emptySettings()) }))}
          onFormPatch={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          onSave={save}
          onPublish={publish}
          onTest={startTest}
          onTranslateLocale={translateLocale}
          translating={translating}
          saving={saving}
        />
      ) : null}
    </div>
  )
}
